import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import BlogArticlesList from 'components/Blog/ArticlesList';
import BlogMainArticles from 'components/Blog/MainArticles';
import BlogSidebar from 'components/Blog/Sidebar';
import Layout from 'components/Layout';

import ArticlesByTagCategoryHead from './Head';
import Heading from './Heading';
import ArticlesByTagCategorySchema from './Schema';

import BLOG from 'constants/blog';

const MostRecentArticles = dynamic(() => import('./MostRecentArticles'), {
  ssr: false,
});

const toMainArticlesTitle = (tagCategoryName) =>
  `Destaques para ${tagCategoryName}`;

const toArticlesFilter = (isCategoryPage) =>
  isCategoryPage ? 'CATEGORY' : 'TAG';

const toArticlesListTitle = (tagName, isCategoryPage) => {
  if (isCategoryPage) {
    return null;
  }

  return `Últimos posts para ${tagName}`;
};

const ArticlesByTagPage = ({
  category: { categoryName, categorySlug } = {},
  isCategoryPage = false,
  mainArticles,
  serverArticlesPage,
  tag: { tagName, tagSlug, relatedCategories } = {},
  topOffers = [],
}) => {
  const isMainArticlesLengthEnoughToShowAsMainContent =
    mainArticles?.length === BLOG.ARTICLES.MAIN_LIMIT;

  return (
    <>
      <ArticlesByTagCategoryHead category={categoryName} tag={tagName} />
      <ArticlesByTagCategorySchema
        category={categoryName}
        slug={categorySlug || tagSlug}
        tag={tagName}
      />
      <Layout
        headingComponent={
          <Heading categoryName={categoryName} tagName={tagName} />
        }
        withGridLayout={false}
        maxWidthOnMobileDevices
      >
        <BlogMainArticles
          articles={
            isCategoryPage || isMainArticlesLengthEnoughToShowAsMainContent
              ? mainArticles
              : []
          }
          relatedCategories={!isCategoryPage ? relatedCategories : []}
          title={
            isCategoryPage || isMainArticlesLengthEnoughToShowAsMainContent
              ? toMainArticlesTitle(categoryName || tagName)
              : null
          }
        />
        <div className="mb-14 grid grid-flow-row grid-cols-4 gap-x-4 md:grid-cols-12">
          <section className="col-span-4 flex flex-col flex-nowrap md:col-span-12 xl:col-span-9">
            <BlogArticlesList
              articlesFilter={toArticlesFilter(isCategoryPage)}
              initialArticlesPage={
                isCategoryPage || isMainArticlesLengthEnoughToShowAsMainContent
                  ? serverArticlesPage
                  : { articles: mainArticles }
              }
              slug={categorySlug || tagSlug}
              title={toArticlesListTitle(tagName, isCategoryPage)}
            />
            {!isCategoryPage &&
              !isMainArticlesLengthEnoughToShowAsMainContent && (
                <MostRecentArticles />
              )}
          </section>
          <BlogSidebar topOffers={topOffers} />
        </div>
      </Layout>
    </>
  );
};

ArticlesByTagPage.propTypes = {
  category: PropTypes.shape({
    categoryName: PropTypes.string,
    categorySlug: PropTypes.string,
  }),
  isCategoryPage: PropTypes.bool,
  mainArticles: PropTypes.arrayOf(
    PropTypes.shape({
      articleDescription: PropTypes.string.isRequired,
      articleId: PropTypes.number.isRequired,
      articleImage: PropTypes.string.isRequired,
      articlePublished: PropTypes.string.isRequired,
      articleModifiedDate: PropTypes.string.isRequired,
      articleSlug: PropTypes.string.isRequired,
      articleThumbnail: PropTypes.string.isRequired,
      articleTitle: PropTypes.string.isRequired,
      categoryName: PropTypes.string.isRequired,
      categorySlug: PropTypes.string.isRequired,
      commentCount: PropTypes.number.isRequired,
      metaTitle: PropTypes.string,
      metaDescription: PropTypes.string,
      articleTags: PropTypes.arrayOf(
        PropTypes.shape({
          tagId: PropTypes.string.isRequired,
          tagName: PropTypes.string.isRequired,
          tagSlug: PropTypes.string.isRequired,
        })
      ),
      userName: PropTypes.string.isRequired,
      userPhoto: PropTypes.string.isRequired,
      userUsername: PropTypes.string.isRequired,
    })
  ).isRequired,
  reviews: PropTypes.shape({
    articles: PropTypes.arrayOf(
      PropTypes.shape({
        articleDescription: PropTypes.string.isRequired,
        articleId: PropTypes.number.isRequired,
        articleImage: PropTypes.string.isRequired,
        articlePublished: PropTypes.string.isRequired,
        articleModifiedDate: PropTypes.string.isRequired,
        articleSlug: PropTypes.string.isRequired,
        articleThumbnail: PropTypes.string.isRequired,
        articleTitle: PropTypes.string.isRequired,
        articleTags: PropTypes.arrayOf(
          PropTypes.shape({
            tagId: PropTypes.string.isRequired,
            tagName: PropTypes.string.isRequired,
            tagSlug: PropTypes.string.isRequired,
          })
        ),
        categoryName: PropTypes.string.isRequired,
        categorySlug: PropTypes.string.isRequired,
        commentCount: PropTypes.number.isRequired,
        metaTitle: PropTypes.string,
        metaDescription: PropTypes.string,
        userName: PropTypes.string.isRequired,
        userPhoto: PropTypes.string.isRequired,
        userUsername: PropTypes.string.isRequired,
      })
    ),
  }),
  topOffers: PropTypes.arrayOf(
    PropTypes.shape({
      offerId: PropTypes.number.isRequired,
      offerTitle: PropTypes.string.isRequired,
      offerPrice: PropTypes.number.isRequired,
      offerOldPrice: PropTypes.number,
      offerPriceType: PropTypes.string.isRequired,
      offerPhoto: PropTypes.string.isRequired,
      offerSlug: PropTypes.string.isRequired,
    })
  ),
  serverArticlesPage: PropTypes.shape({
    after: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sort: PropTypes.string.isRequired,
    before: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    articles: PropTypes.arrayOf(
      PropTypes.shape({
        articleDescription: PropTypes.string.isRequired,
        articleId: PropTypes.number.isRequired,
        articleImage: PropTypes.string.isRequired,
        articlePublished: PropTypes.string.isRequired,
        articleSlug: PropTypes.string.isRequired,
        articleThumbnail: PropTypes.string.isRequired,
        articleTitle: PropTypes.string.isRequired,
        categoryName: PropTypes.string.isRequired,
        categorySlug: PropTypes.string.isRequired,
        commentCount: PropTypes.number.isRequired,
        metaTitle: PropTypes.string,
        metaDescription: PropTypes.string,
        articleTags: PropTypes.arrayOf(
          PropTypes.shape({
            tagId: PropTypes.string.isRequired,
            tagName: PropTypes.string.isRequired,
            tagSlug: PropTypes.string.isRequired,
          })
        ),
        userName: PropTypes.string.isRequired,
        userPhoto: PropTypes.string.isRequired,
        userUsername: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  tag: PropTypes.shape({
    tagName: PropTypes.string,
    tagSlug: PropTypes.string,
    relatedCategories: PropTypes.arrayOf(
      PropTypes.shape({
        categoryName: PropTypes.string.isRequired,
        categorySlug: PropTypes.string.isRequired,
      })
    ),
  }),
};

export default ArticlesByTagPage;
